import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box, Stack, Button, Avatar, Menu, MenuItem, Typography, Divider,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import api, { tokens } from "../api";

/*
 * The two products, as two tabs.
 *
 * They are sold separately and to overlapping but different people: one licenses the
 * SCADA itself, the other the assistant that runs on it. A single list with a column
 * saying which is which would read as one product with a confusing field.
 */
const TABS = [
    { label: "MatrixIQ AI", path: "/admin/accounts" },
    { label: "MatrixIQ Licence", path: "/admin/licences" },
    { label: "Coupons", path: "/admin/coupons" },
];

/*
 * The staff header, shared by every admin screen.
 *
 * One component rather than a copy per page: the buttons that used to sit above each
 * table drifted apart as pages were added, and a header that moves between screens is
 * what makes a product feel like three products.
 *
 * Where you can go is in the header; what you can do is on the page. So Accounts and Chat
 * history are links here, and nothing that changes data is - an action button in a header
 * is reachable from screens it has nothing to do with.
 */


/** Whoever signed in, remembered at login so the header can name them. */
export const staff = {
    get: () => {
        try { return JSON.parse(sessionStorage.getItem("mx_console_user") || "null"); }
        catch { return null; }
    },
    set: (u) => sessionStorage.setItem("mx_console_user", JSON.stringify(u)),
    clear: () => sessionStorage.removeItem("mx_console_user"),
};

export default function AdminHeader() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [menu, setMenu] = useState(null);
    const [me, setMe] = useState(staff.get());
    const email = me?.email || "";

    /*
     * Ask the server who this is when the browser does not already know - a session opened
     * before logins were remembered, or simply a reload. Without this the menu says
     * "Staff", which is true of everyone and tells the person nothing.
     */
    useEffect(() => {
        if (email) return;
        api.get("/console/me")
            .then(({ data }) => { staff.set(data.user); setMe(data.user); })
            .catch(() => { });
    }, [email]);

    const logout = () => {
        tokens.console.clear();
        staff.clear();
        navigate("/admin");
    };


    return (
        <Box component="header" sx={{
            position: "sticky", top: 0, zIndex: 10,
            borderBottom: "1px solid rgba(255,255,255,.07)",
            bgcolor: "rgba(7,18,11,.72)", backdropFilter: "blur(10px)",
        }}>
            <Stack direction="row" spacing={2}
                sx={{ alignItems: "center", px: { xs: 2, md: 4, xl: 6 }, py: 1, minHeight: 58 }}>
                <Box component="img" src="/assets/iotmatrix-white.png" alt="iotmatrix"
                    onClick={() => navigate("/admin/accounts")}
                    sx={{ height: { xs: 26, sm: 32 }, cursor: "pointer" }} />

                <Box sx={{ flex: 1 }} />

                <Stack direction="row" spacing={1} sx={{ mr: { xs: 1, md: 2 } }}>
                    {TABS.map((t) => {
                        const on = pathname.startsWith(t.path);
                        return (
                            <Button key={t.path} onClick={() => navigate(t.path)} sx={{
                                borderRadius: 999, px: 2,
                                color: on ? "#fff" : "rgba(255,255,255,.6)",
                                fontWeight: on ? 700 : 500,
                                bgcolor: on ? "rgba(255,255,255,.08)" : "transparent",
                                "&:hover": { bgcolor: "rgba(255,255,255,.1)" },
                            }}>
                                {t.label}
                            </Button>
                        );
                    })}
                </Stack>

                <Avatar onClick={(e) => setMenu(e.currentTarget)}
                    sx={{
                        width: 34, height: 34, cursor: "pointer",
                        bgcolor: "#2E7D20", color: "#fff", mr: .5,
                    }}>
                    <PersonIcon sx={{ fontSize: 20 }} />
                </Avatar>
            </Stack>

            <Menu open={Boolean(menu)} anchorEl={menu} onClose={() => setMenu(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1, minWidth: 230, bgcolor: "#0C1A12", color: "#fff",
                            border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
                        },
                    },
                }}>
                <Box sx={{ px: 2, py: 1.2 }}>
                    <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 12 }}>Signed in as</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{email || "…"}</Typography>
                </Box>
                <Divider sx={{ borderColor: "rgba(255,255,255,.1)" }} />
                <MenuItem onClick={logout} sx={{ gap: 1.2, py: 1.1 }}>
                    <LogoutIcon fontSize="small" /> Logout
                </MenuItem>
            </Menu>
        </Box>
    );
}

/** The page body every admin screen sits in: the dark background, full width. */
export const adminPage = {
    minHeight: "100vh", color: "#fff",
    background:
        "radial-gradient(900px 520px at 12% 0%, rgba(46,125,32,.3), transparent 62%),"
        + "radial-gradient(760px 540px at 88% 100%, rgba(20,90,60,.26), transparent 64%), #07120B",
};
